import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import type { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAsyncStateSupabaseCallback } from "../../utils/hooks/use-async-state-callback";
import type { UUID } from "../../utils/uuid";
import yup from "../../utils/yup";
import { CeTextInput } from "../generic/components/form-fields/ce-text-field";
import { SupabaseFolderService } from "./server/folder-service";

export type CreateFolderData = {
  parentFolderId: UUID | null;
};

export const CreateFolderModal: FC<{
  open: boolean;
  onClose: () => void;
  reloadFolders: () => void;
  parentFolderId: UUID | null;
}> = ({ open, onClose, reloadFolders, parentFolderId }) => {
  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: { name: "" },
    resolver: yupResolver(
      yup.object({
        name: yup.string().required(t("Enter the folder name")).label(t("Name")),
      }),
    ),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const { loading: saving, load: submit } = useAsyncStateSupabaseCallback(
    async ({ supabase }, { name }: { name: string }) => {
      const service = new SupabaseFolderService(supabase);

      const { error } = await service.insert({
        parent_folder_id: parentFolderId,
        name,
      });

      if (error) {
        console.error(error);

        if (error.message.includes("folder_unique_name")) {
          toast.error(t("Folder name must be unique"));
        } else {
          toast.error(t("Error when creating folder"));
        }

        return false;
      }

      toast.custom(<Alert severity="success">{t(`Folder '${name}' created`)}</Alert>, {
        duration: 3000,
      });
      reloadFolders();
      onClose();
      reset();
      return true;
    },
    [t, onClose, reset, parentFolderId],
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <FormProvider {...methods}>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form id="custom-script-form" onSubmit={handleSubmit(submit)}>
          <DialogTitle>{t("Create new folder")}</DialogTitle>
          <DialogContent>
            <CeTextInput
              name="name"
              margin="dense"
              label={t("Name")}
              fullWidth
              {...(errors.name && { helperText: errors.name.message })}
            />
          </DialogContent>
          <DialogActions>
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: "100%", p: 1, justifyContent: "flex-end" }}
            >
              <Button
                data-analytics-id="create-folder-cancel"
                variant="text"
                color="error"
                onClick={onClose}
              >
                {t("Cancel")}
              </Button>
              <Button
                data-analytics-id="create-folder-confirm"
                variant="text"
                type="submit"
                loading={saving}
              >
                {t("Create folder")}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
